/**
 * SidebarItem - Generic sidebar item component
 *
 * A unified component for displaying items in the sidebar (dashboards, trackers,
 * enrichments, alerts, investigations, widgets, conversations).
 *
 * Features:
 * - Consistent styling across all resource types
 * - Multi-select support (checkbox, Cmd+click, Shift+click)
 * - Drag & drop support (for reordering and grouping)
 * - Hover actions
 * - Active state indication
 * - Status indicators
 * - Inline renaming support
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import {
  FaCheckSquare,
  FaRegSquare,
  FaGripVertical,
  FaFolder,
} from "react-icons/fa";
import { ResourceType } from "@/models/resource-types";
import { useMultiSelect, SelectableItemType } from "@/hooks/use-multi-select";
import { useResourceDrag } from "@/hooks/use-resource-drag";
import { DragPayload } from "@/utils/drag-types";
import { MotionDiv } from "./motion-types";

// ============================================================================
// Types
// ============================================================================

export interface SidebarItemProps {
  /** Unique identifier */
  id: string;
  /** Display name */
  name: string;
  /** Resource type for drag & drop and multi-select */
  resourceType: ResourceType;

  // Visual
  /** Icon to display (React node) */
  icon: React.ReactNode;
  /** Optional color indicator (small dot) */
  color?: string;
  /** Optional subtitle (displayed below name, smaller text) */
  subtitle?: string;
  /** Optional status indicator (displayed on the right when not hovered) */
  statusIndicator?: React.ReactNode;
  /** Optional indicator next to the name (e.g., unsaved changes dot) */
  nameIndicator?: React.ReactNode;

  // State
  /** Whether this item is currently active/selected in the view */
  isActive?: boolean;
  /** Whether the item is disabled (reduced opacity) */
  isDisabled?: boolean;

  // Actions
  /** Click handler */
  onClick: () => void;
  /** Context menu handler (right-click) */
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Hover actions (buttons shown on hover) */
  hoverActions?: React.ReactNode;

  // Multi-select
  /** Whether this item is selected (override internal state) */
  isSelected?: boolean;
  /** Ordered list of all item IDs for range selection */
  orderedIds?: string[];
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;

  // Drag & drop
  /** Whether drag is enabled (default: true) */
  enableDrag?: boolean;
  /** Callback when another item is dropped on this one to create a group */
  onDropToCreateGroup?: (draggedId: string, targetId: string) => void;
  /** Optional drag payload for split view */
  dragPayload?: DragPayload;

  // Renaming
  /** Whether the item is currently being renamed */
  isRenaming?: boolean;
  /** Callback when rename is submitted */
  onRenameSubmit?: (newName: string) => void;
  /** Callback when rename is cancelled */
  onRenameCancel?: () => void;

  // Customization
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the color indicator (default: true if color is provided) */
  showColorIndicator?: boolean;
}

// ============================================================================
// Component
// ============================================================================

const SidebarItem: React.FC<SidebarItemProps> = ({
  id,
  name,
  resourceType,
  icon,
  color,
  subtitle,
  statusIndicator,
  nameIndicator,
  isActive = false,
  isDisabled = false,
  onClick,
  onContextMenu,
  hoverActions,
  isSelected: isSelectedProp,
  orderedIds = [],
  onMultiSelectContextMenu,
  enableDrag = true,
  onDropToCreateGroup,
  dragPayload,
  isRenaming = false,
  onRenameSubmit,
  onRenameCancel,
  className = "",
  showColorIndicator,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [renameValue, setRenameValue] = useState(name);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Multi-select hook
  const {
    isSelected: isSelectedFromStore,
    handleItemClick,
    hasSelection,
    toggleSelection,
  } = useMultiSelect();

  // Use prop if provided, otherwise check store
  const isSelected =
    isSelectedProp !== undefined ? isSelectedProp : isSelectedFromStore(id);

  // Drag & drop hook
  const { itemRef, dragProps, isDragging, isGroupMode, isReorderMode } =
    useResourceDrag({
      resourceId: id,
      resourceType,
      displayName: name,
      color,
      enabled: enableDrag && !isRenaming,
      onDropToCreateGroup,
      dragPayload,
    });

  // Focus input when entering rename mode
  useEffect(() => {
    if (isRenaming && inputRef.current) {
      setRenameValue(name);
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming, name]);

  // Cast resourceType to SelectableItemType for multi-select
  // Not all resource types support multi-select (e.g., "view")
  const selectableType = resourceType as SelectableItemType;

  // Click handler with multi-select support
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isRenaming) return;

      // Check for multi-select modifiers (Ctrl/Cmd or Shift)
      const handled = handleItemClick(id, selectableType, e, orderedIds);
      if (handled) {
        return;
      }

      onClick();
    },
    [id, selectableType, orderedIds, handleItemClick, onClick, isRenaming],
  );

  // Context menu handler
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (isRenaming) return;
      e.preventDefault();

      // If this item is selected and we have a multi-select context menu handler, use it
      if (isSelected && hasSelection && onMultiSelectContextMenu) {
        onMultiSelectContextMenu(e);
        return;
      }

      onContextMenu?.(e);
    },
    [
      isSelected,
      hasSelection,
      onMultiSelectContextMenu,
      onContextMenu,
      isRenaming,
    ],
  );

  // Checkbox click handler
  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      toggleSelection(id, selectableType);
    },
    [id, selectableType, toggleSelection],
  );

  // Rename handlers
  const handleRenameSubmit = useCallback(async () => {
    const trimmedValue = renameValue.trim();
    if (!trimmedValue || trimmedValue === name) {
      onRenameCancel?.();
      return;
    }

    setIsSubmitting(true);
    try {
      await onRenameSubmit?.(trimmedValue);
    } finally {
      setIsSubmitting(false);
    }
  }, [renameValue, name, onRenameSubmit, onRenameCancel]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleRenameSubmit();
      } else if (e.key === "Escape") {
        e.preventDefault();
        onRenameCancel?.();
      }
    },
    [handleRenameSubmit, onRenameCancel],
  );

  // Determine if we should show the color indicator
  const shouldShowColor = showColorIndicator ?? !!color;

  // Build class names
  const baseClasses =
    "relative flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all hover:translate-x-0.5";
  const hoverClasses =
    "hover:bg-[var(--arc-sidebar-accent-glow)] text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)]";
  const activeClasses = isActive
    ? "arc-sidebar-item-active text-[var(--arc-sidebar-text-primary)]"
    : "";
  const disabledClasses = isDisabled ? "opacity-60" : "";
  const draggingClasses = isDragging ? "opacity-50" : "";
  const selectedClasses = isSelected
    ? "bg-[var(--arc-accent)]/15 ring-1 ring-[var(--arc-accent)]/50"
    : "";
  const reorderClasses = isReorderMode
    ? "border-t-2 border-t-[var(--arc-accent)]"
    : "";
  const groupModeClasses = isGroupMode
    ? "bg-[var(--arc-accent)]/20 ring-2 ring-[var(--arc-accent)] ring-inset"
    : "";

  const combinedClasses = [
    baseClasses,
    hoverClasses,
    activeClasses,
    disabledClasses,
    draggingClasses,
    selectedClasses,
    reorderClasses,
    groupModeClasses,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={itemRef as React.RefObject<HTMLDivElement>}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={combinedClasses}
      {...dragProps}
    >
      {/* Group mode indicator - folder icon overlay */}
      {isGroupMode && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <div className="bg-[var(--arc-sidebar-bg)] rounded-full p-1.5 shadow-lg">
            <FaFolder
              className="text-[var(--arc-accent)] animate-pulse"
              size={16}
            />
          </div>
        </div>
      )}

      {/* Drag handle (when hovering and no selection) */}
      {isHovered && !hasSelection && !isGroupMode && enableDrag && (
        <div className="flex-shrink-0 text-[var(--arc-sidebar-text-tertiary)] cursor-grab active:cursor-grabbing">
          <FaGripVertical size={10} />
        </div>
      )}

      {/* Selection checkbox (when hovering or when items are selected) */}
      {(isHovered || hasSelection) && !isGroupMode && !isRenaming && (
        <div
          className="flex-shrink-0 text-[var(--arc-accent)] cursor-pointer"
          onClick={handleCheckboxClick}
        >
          {isSelected ? (
            <FaCheckSquare size={12} />
          ) : (
            <FaRegSquare size={12} className="opacity-50 hover:opacity-100" />
          )}
        </div>
      )}

      {/* Color Indicator */}
      {shouldShowColor && color && (
        <div
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: color }}
        />
      )}

      {/* Icon */}
      <div
        className={`flex-shrink-0 w-4 h-4 flex items-center justify-center ${
          isActive ? "arc-sidebar-icon-active" : ""
        }`}
      >
        {icon}
      </div>

      {/* Name and subtitle - or rename input */}
      {isRenaming ? (
        <div className="flex-1 flex items-center gap-1">
          <input
            ref={inputRef}
            type="text"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={handleRenameSubmit}
            disabled={isSubmitting}
            className="flex-1 bg-[var(--arc-surface-elevated)] border border-[var(--arc-accent)] text-[var(--arc-sidebar-text-primary)] rounded px-2 py-0.5 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-[var(--arc-accent)]"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      ) : (
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[var(--arc-sidebar-text-primary)] text-sm font-medium truncate">
              {name}
            </span>
            {nameIndicator}
          </div>
          {subtitle && (
            <span className="text-[10px] text-[var(--arc-sidebar-text-tertiary)] truncate block">
              {subtitle}
            </span>
          )}
        </div>
      )}

      {/* Hover Actions */}
      <AnimatePresence>
        {isHovered && !isGroupMode && hoverActions && (
          <MotionDiv
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1"
          >
            {hoverActions}
          </MotionDiv>
        )}
      </AnimatePresence>

      {/* Status indicator (when not hovered and not in group mode) */}
      {!isHovered && !isGroupMode && statusIndicator}
    </div>
  );
};

SidebarItem.displayName = "SidebarItem";

export default SidebarItem;

// ============================================================================
// Helper Components for Hover Actions
// ============================================================================

export interface SidebarItemActionProps {
  onClick: (e: React.MouseEvent) => void;
  title: string;
  icon: React.ReactNode;
  variant?: "default" | "danger" | "success";
}

/**
 * SidebarItemAction - A button for hover actions in SidebarItem
 */
export const SidebarItemAction: React.FC<SidebarItemActionProps> = ({
  onClick,
  title,
  icon,
  variant = "default",
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick(e);
  };

  const variantClasses = {
    default:
      "hover:bg-[var(--arc-sidebar-accent-glow)] hover:text-[var(--arc-sidebar-text-primary)]",
    danger: "hover:bg-red-500/30 hover:text-red-400",
    success: "text-green-500 hover:text-green-400",
  };

  return (
    <button
      onClick={handleClick}
      className={`p-1 rounded transition-colors ${variantClasses[variant]}`}
      title={title}
    >
      {icon}
    </button>
  );
};

SidebarItemAction.displayName = "SidebarItemAction";
